/**
 * `World<P>` — the per-encounter spatial **port**. `Space<P>` is the static,
 * grid-agnostic geometry; `World<P>` is that geometry *plus* the two queries a
 * live encounter answers from its own state — the map's extent (`inBounds`) and
 * footprint-to-footprint distance (`regionDistance`). The engine binds ONE
 * `World<P>` and never branches on grid mode; positions stay opaque (`P`).
 *
 * **Sized planar-now, superset-later.** The port is designed against the 3D
 * superset (vertical queries, more directional patterns), but those slots are
 * deliberately **not** on this interface until a real consumer exists —
 * building them speculatively risks over-fitting. When one lands it widens
 * `World<P>` here; the 2D adapter answers it degenerately.
 *
 * `inBounds` is a **world property** — the map knows its own extent (a hex disk
 * measured by `mapRadius`, or a square rectangle `gridWidth × gridHeight`),
 * rather than the rules layer re-deriving it from a hex-shaped scalar.
 */
import type { Region, Space } from "./space";

/**
 * The two directional area templates. A grid supplies them as pure geometry;
 * the square world binds `ray`/`cone`, the hex world binds `hexRay`/`hexCone`.
 * `origin` is the template apex, `toward` a point that fixes the aim direction,
 * `len` the reach in spaces.
 */
export type Patterns<P> = {
  /** A one-wide LINE from `origin` toward `toward`, length `len`, apex excluded. */
  linePattern(origin: P, toward: P, len: number): Region<P>;
  /** A 90° CONE wedge from `origin` toward `toward`, length `len`, apex excluded. */
  conePattern(origin: P, toward: P, len: number): Region<P>;
};

export type World<P> = Space<P> & Patterns<P> & {
  /** Map membership — whether `p` is on the playable map. */
  inBounds(p: P): boolean;
  /**
   * Footprint-to-footprint minimum grid distance: the min over the cross-product
   * of two regions' cells (the "from any edge" measure, RAW p.64). `Infinity`
   * when either region is empty — the caller treats it as "no answer".
   */
  regionDistance(a: Region<P>, b: Region<P>): number;
};

/**
 * The generic {@link World.regionDistance} — min grid distance over the
 * cross-product of two regions' cells. `Infinity` when either is empty. Lives on
 * the port so the geometry stays in the adapter rather than as hex algebra in
 * the rules layer.
 */
export function regionDistance<P>(space: Space<P>, a: Region<P>, b: Region<P>): number {
  let min = Infinity;
  for (const x of a.cells()) {
    for (const y of b.cells()) {
      const d = space.distance(x, y);
      if (d < min) min = d;
    }
  }
  return min;
}

/**
 * Grow a static {@link Space} into a per-encounter {@link World} by supplying the
 * two things the static geometry can't know: the map's extent (`inBounds`) and the
 * grid's directional {@link Patterns} (line/cone — per-grid geometry the generic
 * `Space` doesn't carry). `regionDistance` is derived generically from
 * `space.distance`; the `Space` methods are delegated unchanged.
 */
export function makeWorld<P>(space: Space<P>, inBounds: (p: P) => boolean, patterns: Patterns<P>): World<P> {
  return {
    ...space,
    inBounds,
    regionDistance: (a, b) => regionDistance(space, a, b),
    linePattern: patterns.linePattern,
    conePattern: patterns.conePattern,
  };
}
